import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import BookSetupModal from "../components/cover/BookSetupModal";
import CoverEditor from "../components/cover/CoverEditor";
import CoverEditorErrorBoundary from "../components/cover/CoverEditorErrorBoundary";
import { Loader2 } from "lucide-react";

const CoverCreator = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [setup, setSetup] = useState(null);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(!!id);
  const [showSetup, setShowSetup] = useState(!id);
  const [tooSmall, setTooSmall] = useState(false);
  // Initial values prefilled from another tool (e.g. front-matter project
  // passed via ?from=<fm_id>). These are merged into BookSetupModal's
  // default form state via the `initial` prop so the user only needs to
  // fill in the fields that aren't already known (page count, paper type).
  const fromFm = searchParams.get("from");
  const [prefilled, setPrefilled] = useState(null);
  const prefillReady = !fromFm || prefilled !== null;

  useEffect(() => {
    const check = () => setTooSmall(window.innerWidth < 900);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Hydrate the setup form from a linked front-matter project when arriving
  // via "Create matching cover" → /cover/new?from=<fm_id>.
  useEffect(() => {
    if (id) return; // editing existing cover; no prefill needed.
    const fromFm = searchParams.get("from");
    if (!fromFm) return;
    api.get(`/frontmatter/${fromFm}`).then(({ data }) => {
      setPrefilled({
        book_title: data.book_title || "",
        subtitle: data.subtitle || "",
        author: data.author || "",
        trim_size: data.trim_size || "6x9",
      });
    }).catch(() => {
      // Silently ignore — the modal will fall back to its defaults.
    });
  }, [id, searchParams]);

  useEffect(() => {
    if (!id) return;
    api
      .get(`/cover/${id}`)
      .then(({ data }) => {
        setProject(data);
        setSetup({
          book_title: data.book_title,
          subtitle: data.subtitle || "",
          author: data.author,
          trim_size: data.trim_size,
          page_count: data.page_count,
          paper_type: data.paper_type,
          spine_width_inches: parseFloat(data.spine_width_inches),
          binding_type: data.binding_type || "paperback",
        });
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        navigate("/dashboard");
      });
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-[#534AB7]" />
      </div>
    );
  }

  if (tooSmall) {
    return (
      <div className="min-h-screen bg-[#FAFAF9] flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <h2 className="text-2xl tracking-tight" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 500 }}>
            The Cover Creator is best on a desktop.
          </h2>
          <p className="mt-3 text-sm text-[#5F5E5A]">
            We'll add a mobile editor later — for now please switch to a screen wider than 900px to design covers.
          </p>
          <button
            onClick={() => navigate("/dashboard")}
            className="mt-6 px-5 py-2.5 rounded-[8px] bg-[#534AB7] text-white text-sm font-medium"
          >
            Back to dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen overflow-hidden bg-[#F1EFE8]">
      {showSetup && prefillReady && (
        <BookSetupModal
          onCancel={() => navigate("/dashboard")}
          onConfirm={(data) => {
            setSetup(data);
            setShowSetup(false);
          }}
          initial={prefilled}
        />
      )}
      {showSetup && !prefillReady && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#2C2C2A]/40 backdrop-blur-sm">
          <Loader2 className="w-6 h-6 animate-spin text-white" />
        </div>
      )}
      {setup && !showSetup && (
        <CoverEditorErrorBoundary>
          <CoverEditor
            key={`${setup.trim_size}|${setup.spine_width_inches}|${setup.page_count}|${setup.binding_type || "paperback"}`}
            setup={setup}
            project={project}
            onResize={async (next) => {
              // Update local state — the `key` above forces CoverEditor to
              // remount so Fabric re-creates each canvas at the new size.
              // The latest canvas JSON is already persisted from the modal
              // (handleSave was awaited before this callback fires), so we
              // reload the project from the backend to pick up the saved
              // panel JSON and the new dimensions are applied on top.
              try {
                const merged = { ...setup, ...next };
                setSetup(merged);
                if (project?.id) {
                  try {
                    const { data } = await api.get(`/cover/${project.id}`);
                    setProject({
                      ...data,
                      trim_size: merged.trim_size,
                      page_count: merged.page_count,
                      paper_type: merged.paper_type,
                      spine_width_inches: merged.spine_width_inches,
                      binding_type: merged.binding_type,
                    });
                  } catch (err) {
                    // Non-fatal: the local setup state is already updated.
                    console.warn("Post-resize project refetch failed:", err);
                  }
                }
              } catch (err) {
                // Any synchronous failure here would otherwise crash the
                // ErrorBoundary. Log and let the user keep working.
                console.error("Resize parent handler failed:", err);
              }
            }}
          />
        </CoverEditorErrorBoundary>
      )}
    </div>
  );
};

export default CoverCreator;
